import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getPdfGeneratedEngagementLeaderboardData } from '../../../API/UtilizationReportingDashboard';
import { constructChartData, downloadDivChart, getTimePeriodLabel, utilizationReportConstants, hasValuesGreaterThanZero, setChartXAxes, getTimePeriodTile, baseHorizontalBarChartOptions } from './utlizationChart';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import ButtonTooltip from '../../Tooltip';
import Mustache from 'mustache';
import ChartDataLabels from "chartjs-plugin-datalabels";
import { createColorPaletteRegistry } from '../ColorRegistry';

PdfGeneratedEngagementLeaderboardChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object
}

function PdfGeneratedEngagementLeaderboardChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState] = useContext(AppContext);
    const chartRef = useRef(null);
    const [data, setData] = useState({});
    const [chartData, setChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const maxUsersToDisplay = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGeneratedEngagementLeaderboard?.Filters?.MaxUsers, appState)?.Value || 10;
    const noDataMessage = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.NoDataMessage, appState )?.Value;
    const tooltipTarget = useRef(null); 
    const [maxXValue, setMaxXValue] = useState(null);
    const [xAxesStepIncrementValue, setXAxesStepIncrementValue] = useState(1);
    const [showPriorPeriodTabTooltip, setShowPriorPeriodTabTooltip] = useState(false);
    const [priorTimePeriodTabAvailable, setPriorTimePeriodTabAvailable] = useState(true);
    const priorPeriodChartTooltipTarget = useRef(null); 
    const chartEnabled = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGeneratedEngagementLeaderboard?.Enabled, appState)?.Value === 'true' || false;
    const [chartColorRegistry] = useState(() => createColorPaletteRegistry(
        JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.ColorPalette, appState)?.Value) || [],
        { storageKey: 'utilizationReportingDashboard:engagementLeaderboard:colorpalette' }
    ));

    ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);
 
    const getData = async () => {
        if(filterState.timePeriodId == undefined) {
            return;
        }

        setIsLoading(true);
        await getPdfGeneratedEngagementLeaderboardData({
                "timeFilter": filterState.timePeriodId,
                "includePriorPeriod": true,
                "maxUsers": maxUsersToDisplay,
                "geography": filterState.selectedGeography,
                "geographyType": filterState.selectedGeographyType,
                "productIds": filterState.selectedProductIds,
                "messageCategory": filterState.messageCategoryTitle
        })        
        .then((responseJson) => {
            setData(responseJson);
            setChartData({labels: responseJson.currentPeriod.labels, datasets: constructChartData( responseJson.currentPeriod.dataSets, chartColorRegistry, appState)});    
            setChartXAxes(responseJson.currentPeriod.dataSets, setXAxesStepIncrementValue, setMaxXValue);
            setPriorTimePeriodTabAvailable(hasValuesGreaterThanZero(responseJson.priorPeriod));
        })
        .catch((err) => {
            showBoundary(err);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    const toggleChartTab = (activeTab, selectedData) => {        
        setChartData({labels: selectedData.labels, datasets: constructChartData(selectedData.dataSets, chartColorRegistry, appState)});  
        setActiveButton(activeTab); 
        setChartXAxes(selectedData.dataSets, setXAxesStepIncrementValue, setMaxXValue);
    }

    ChartJS.register({
        id: 'noDataChartPlugin',
        beforeDraw: (chart) => {
            const centerConfig = chart.options.plugins.noDataChartPlugin;      
            const hasData = chart?.data?.datasets?.some(dataset => dataset?.data?.length >0);
            const messageText = centerConfig.text || "";

            if(!hasData) {
                const { ctx, chartArea } = chart;
                const { top, bottom, left, right } = chartArea;
                const centerX = (left + right) / 2;
                const centeryY = (top + bottom) /2;
        
                ctx.save();
                ctx.textAlighn = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = 'gray';
                ctx.font = '16px sans-serif';
                ctx.fillText(messageText, centerX, centeryY);
                ctx.restore();
            }
        }
      });

    const chartOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: baseHorizontalBarChartOptions.scales.y,
            x: {
                max: maxXValue,
                ...baseHorizontalBarChartOptions.scales.x,
                ticks: {
                    beginAtZero: true,
                    stepSize: xAxesStepIncrementValue,
                    ...baseHorizontalBarChartOptions.scales.x.ticks       
                }
            }
        },
        layout: baseHorizontalBarChartOptions.layout,
        plugins: {
            noDataChartPlugin: {
                text: noDataMessage
            },
            datalabels: baseHorizontalBarChartOptions.datalabels,
            legend: baseHorizontalBarChartOptions.legend,
            title: {
                ...baseHorizontalBarChartOptions.title,
                text: [
                    getTimePeriodLabel(filterState.timePeriodId, activeButton, false),                    
                    filterState.allProducts ? Mustache.render(getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllProducts?.Label, appState)?.Value || null, { count: appState?.sellSheetProductDetails?.length }) : filterState.selectedProductIds?.map(id => {
                        const product = appState?.sellSheetProductDetails.find(p => p.clientProductId === id);
                        return product.name || `Unknow (${id})`;
                    })
                    .join(', '),
                    !filterState.selectedGeography ? getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllGeographies?.Label, appState)?.Value || null : filterState.selectedGeographyLabel,
                    filterState.messageCategoryTitle
                ]
            },
            tooltip: baseHorizontalBarChartOptions.tooltip,  
        },
        resizeDelay:  200,
    };


    useEffect(() => {
        if (chartEnabled && appState?.config) {                
            getData();            
        }
    }, [filterState.selectedProductIds, filterState.timePeriodId, filterState.selectedGeography, filterState.messageCategoryId]);


    let content;
    if (isLoading === null || isLoading) {
        content =  (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content = (
            <> 
                <div id="pdfGeneratedEngagementLeaderboard-ChartsArea" className='chart-canvas-wrapper chart-canvas-wrapper--tall'>
                    <Bar className={`chart ${isCollapsed ? 'collapsed' : ''}`} ref={chartRef} options={chartOptions} data={chartData} /> 
                </div>               
                <div className='reportButtons-container'>   
                    <Button 
                        className='reportTextButton' 
                        onClick={() => downloadDivChart('pdfGeneratedEngagementLeaderboard-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGeneratedEngagementLeaderboard?.Title, appState)?.Value || null)}
                        >
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>
                </div>                
                <div className='reportTabButton-container'>
                    <div className='wrapper'
                        >
                        <Button 
                            className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive" }`}
                            onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod);}}
                            >
                                {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({getTimePeriodTile(filterState.timePeriodId, filterState.timePeriodTitle, false)})
                        </Button>
                        
                    </div>
                    <div className='wrapper'
                        ref={priorPeriodChartTooltipTarget} 
                        onMouseOver={() => setShowPriorPeriodTabTooltip(!priorTimePeriodTabAvailable)}
                        onFocus={() => setShowPriorPeriodTabTooltip(!priorTimePeriodTabAvailable)}
                        onMouseOut={() => setShowPriorPeriodTabTooltip(false)}   
                        onBlur={() => setShowPriorPeriodTabTooltip(false)} 
                    >
                        <Button 
                            disabled={!priorTimePeriodTabAvailable}
                            className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive" }`}
                            onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod);}}
                            >
                                {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                        </Button>
                        <ButtonTooltip               
                                showTooltip={showPriorPeriodTabTooltip}
                                text={`${getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Disabled?.ToolTip, appState)?.Value}`}                    
                                ref={priorPeriodChartTooltipTarget}
                                index={0} />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
        {chartEnabled &&
        <div className='chart-panel'>
            <div className='chart-header'>
                <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={()=> {setIsCollapsed(!isCollapsed)}}>
                    {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGeneratedEngagementLeaderboard?.Title, appState)?.Value || null}
                    <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGeneratedEngagementLeaderboard?.ToolTip, appState )?.Value} />  
                </Button>   
            </div>
            <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>                
                {content}                
            </div>
        </div>
}
        </>
    );
}

export default PdfGeneratedEngagementLeaderboardChart;